import { useCallback, useEffect, useRef } from 'react';
import { type BaseQueryFn, type QueryDefinition, type QueryEndpointMethodsAndHooks } from '@adv-frontend/modules-query';

type Options<Model extends object, ReducerPath extends string = string, TagTypes extends string = never> = {
    /**
     * Функция для проверки данных, которые переданы в компонент
     * Если они соответствуют этому условию то хук будет возвращать их вместе с состоянием запроса
     */
    validatePreviewData: (previewData: unknown) => previewData is Partial<Model>;
    /**
     * Эндпоинт для запроса данных
     */
    endpoint: QueryEndpointMethodsAndHooks<QueryDefinition<{ id: string }, BaseQueryFn, TagTypes, Model, ReducerPath>>;
};

type Returns<Model extends object> = [
    {
        isFetching: boolean;
        isSuccess: boolean;
        isError: boolean;
        error: unknown;
        data: Model | undefined;
    },
    Partial<Model> | undefined,
    () => void,
];

/*
 * Создает хук, который возвращает данные по id если они были получены из бекенда и previewData - которые были переданы в компонент
 * Если previewData не являются данными которые фронт ожидает от бекенда - то возвращается undefined
 */
export function createGetByIdHookWithPreviewData<
    Model extends object,
    ReducerPath extends string = string,
    TagTypes extends string = never,
>({ validatePreviewData, endpoint }: Options<Model, ReducerPath, TagTypes>) {
    return function useGetByIdWithPreviewData(id: string, previewData?: unknown | Partial<Model>): Returns<Model> {
        // используем status так как isSuccess: true если идет новый запрос с новыми данными
        // при чем возвращается предыдущее значение в data
        const [fetch, { status, data, error }, { lastArg }] = endpoint.useLazyQuery();

        const ref = useRef<Record<string, Partial<Model>>>({});

        if (status === 'fulfilled' && data) {
            ref.current[lastArg?.id] = data;
        }

        if (!ref.current[id] && validatePreviewData(previewData)) {
            ref.current[id] = previewData;
        }

        const refetch = useCallback(() => {
            fetch({ id });
        }, [fetch, id]);

        useEffect(() => {
            if (lastArg?.id !== id) {
                refetch();
            }
        }, [id, lastArg, refetch]);

        return [
            id === lastArg?.id
                ? {
                      isFetching: status === 'pending',
                      isSuccess: status === 'fulfilled',
                      isError: status === 'rejected',
                      error,
                      data: status === 'fulfilled' ? data : undefined,
                  }
                : {
                      isFetching: true,
                      isSuccess: false,
                      isError: false,
                      error: undefined,
                      data: undefined,
                  },
            ref.current[id],
            refetch,
        ];
    };
}
